"use client";

import NextImage from "next/image";
import type * as React from "react";
import { useState } from "react";
import { cn } from "../../lib/utils";
import { AspectRatio } from "../ui/aspect-ratio";

// Preset size mappings (convenience)
const PRESET_SIZES = {
  xs: 64,
  sm: 96,
  md: 192,
  lg: 384,
  xl: 512,
} as const;

// Rounded variants
const ROUNDED_CLASSES = {
  none: "",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full",
} as const;

type AspectRatioValue =
  | "1:1"
  | "4:3"
  | "3:2"
  | "16:9"
  | "21:9"
  | "9:16"
  | "2:3"
  | number;

interface ImageProps {
  alt: string;
  aspectRatio?: AspectRatioValue;

  // Styling
  className?: string;
  containerClassName?: string;

  // Error handling
  fallback?: React.ReactNode;

  // Visual styling
  fit?: "cover" | "contain";
  height?: number | string;

  // Behavioral
  loading?: "lazy" | "eager";
  objectPosition?: string;
  onClick?: () => void;
  onError?: () => void;
  openOnClick?: boolean;
  preset?: keyof typeof PRESET_SIZES;
  priority?: boolean;
  rounded?: keyof typeof ROUNDED_CLASSES;
  // Required
  src: string;

  // Sizing modes (mutually exclusive)
  width?: number | string;
}

interface ImageModalProps {
  alt: string;
  onClose: () => void;
  src: string;
}

type SizingMode =
  | {
      mode: "explicit";
      width: number | string;
      height: number | string;
      useAspectRatio: false;
    }
  | {
      mode: "width-constrained";
      width: number | string;
      ratio: number;
      useAspectRatio: true;
    }
  | {
      mode: "height-constrained";
      height: number | string;
      ratio: number;
      useAspectRatio: true;
    }
  | {
      mode: "responsive";
      ratio: number;
      useAspectRatio: true;
    }
  | {
      mode: "intrinsic";
      useAspectRatio: false;
    };

function parseAspectRatio(ratio: AspectRatioValue): number {
  if (typeof ratio === "number") {
    return ratio;
  }

  const [w, h] = ratio.split(":").map(Number);
  if (!(w && h)) {
    return 1; // fallback to 1:1 aspect ratio if invalid format
  }
  return w / h;
}

function calculateDimensions(props: ImageProps): SizingMode {
  const { width, height, aspectRatio, preset } = props;

  // Apply preset if provided (preset acts as width)
  const effectiveWidth = preset ? PRESET_SIZES[preset] : width;

  // Mode A: Explicit dimensions
  if (effectiveWidth && height) {
    return {
      mode: "explicit",
      width: effectiveWidth,
      height,
      useAspectRatio: false,
    };
  }

  // Mode B: Width-constrained
  if (effectiveWidth && aspectRatio) {
    return {
      mode: "width-constrained",
      width: effectiveWidth,
      ratio: parseAspectRatio(aspectRatio),
      useAspectRatio: true,
    };
  }

  // Mode B: Height-constrained
  if (height && aspectRatio) {
    return {
      mode: "height-constrained",
      height,
      ratio: parseAspectRatio(aspectRatio),
      useAspectRatio: true,
    };
  }

  // Mode C: Responsive
  if (aspectRatio) {
    return {
      mode: "responsive",
      ratio: parseAspectRatio(aspectRatio),
      useAspectRatio: true,
    };
  }

  // Fallback: intrinsic sizing
  return {
    mode: "intrinsic",
    useAspectRatio: false,
  };
}

interface ContainerStyleConfig {
  aspectRatio?: number;
  containerStyle?: React.CSSProperties;
  useAspectRatioWrapper: boolean;
}

function formatDimensionValue(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function getContainerStyles(dimensions: SizingMode): ContainerStyleConfig {
  switch (dimensions.mode) {
    case "explicit":
      return {
        containerStyle: {
          width: formatDimensionValue(dimensions.width),
          height: formatDimensionValue(dimensions.height),
        },
        useAspectRatioWrapper: false,
      };

    case "width-constrained":
      return {
        containerStyle: { width: formatDimensionValue(dimensions.width) },
        useAspectRatioWrapper: true,
        aspectRatio: dimensions.ratio,
      };

    case "height-constrained": {
      const heightValue = dimensions.height;
      const isNumber = typeof heightValue === "number";
      const calculatedWidth = isNumber ? heightValue * dimensions.ratio : 0;

      return {
        containerStyle: {
          width: isNumber
            ? `${calculatedWidth}px`
            : `calc(${heightValue} * ${dimensions.ratio})`,
          height: formatDimensionValue(heightValue),
        },
        useAspectRatioWrapper: false,
      };
    }

    case "responsive":
      return {
        useAspectRatioWrapper: true,
        aspectRatio: dimensions.ratio,
      };

    case "intrinsic":
      return {
        useAspectRatioWrapper: false,
      };

    default:
      return {
        useAspectRatioWrapper: false,
      };
  }
}

interface ImageContentProps {
  alt: string;
  imageClasses: string;
  imageError: boolean;
  imageLoading: "lazy" | "eager" | undefined;
  imageStyle: React.CSSProperties;
  onError: () => void;
  priority: boolean;
  renderFallback: () => React.ReactNode;
  src: string;
}

function ImageContent(props: ImageContentProps) {
  const {
    imageError,
    renderFallback,
    src,
    alt,
    imageClasses,
    imageStyle,
    imageLoading,
    priority,
    onError,
  } = props;

  return imageError ? (
    renderFallback()
  ) : (
    <NextImage
      alt={alt}
      className={imageClasses}
      fill
      loading={imageLoading}
      onError={onError}
      priority={priority}
      src={src}
      style={imageStyle}
    />
  );
}

interface ImageWrapperProps {
  children: React.ReactNode;
  className?: string;
  isClickable: boolean;
  onClick: () => void;
  style?: React.CSSProperties;
}

function ImageWrapper(props: ImageWrapperProps) {
  const { isClickable, onClick, className, style, children } = props;

  if (!isClickable) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <button
      className={className}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      style={style}
      type="button"
    >
      {children}
    </button>
  );
}

function Image({
  src,
  alt,
  width,
  height,
  aspectRatio,
  preset,
  fit = "cover",
  objectPosition,
  rounded = "md",
  loading = "lazy",
  priority = false,
  onClick,
  openOnClick = false,
  fallback,
  onError,
  className,
  containerClassName,
}: ImageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleClick = () => {
    if (openOnClick) {
      setIsOpen(true);
    }
    if (onClick) {
      onClick();
    }
  };

  const handleImageError = () => {
    setImageError(true);
    if (onError) {
      onError();
    }
  };

  const dimensions = calculateDimensions({
    src,
    alt,
    width,
    height,
    aspectRatio,
    preset,
  });
  const roundedClass = ROUNDED_CLASSES[rounded];
  const isClickable = Boolean(openOnClick || onClick);
  // When priority is true, don't pass loading prop (Next.js doesn't allow both)
  const imageLoading = priority ? undefined : loading;

  // Build container classes
  const baseContainerClasses = cn(
    "relative overflow-hidden",
    roundedClass,
    isClickable && "cursor-pointer",
    className,
    containerClassName
  );

  // Build image classes
  const imageClasses = cn(fit === "cover" ? "object-cover" : "object-contain");

  // Build image style
  const imageStyle: React.CSSProperties = objectPosition
    ? { objectPosition }
    : {};

  // Render error fallback
  const renderFallback = () => {
    if (fallback) {
      return (
        <div className="flex size-full items-center justify-center bg-muted">
          {fallback}
        </div>
      );
    }
    return (
      <div className="flex size-full items-center justify-center bg-muted">
        <span className="text-muted-foreground text-sm">
          {alt.slice(0, 2).toUpperCase()}
        </span>
      </div>
    );
  };

  // Get container styling configuration
  const containerConfig = getContainerStyles(dimensions);

  // Build wrapper for image content with interactive behavior
  const content = (
    <ImageWrapper
      className={baseContainerClasses}
      isClickable={isClickable}
      onClick={handleClick}
      style={containerConfig.containerStyle}
    >
      <ImageContent
        alt={alt}
        imageClasses={imageClasses}
        imageError={imageError}
        imageLoading={imageLoading}
        imageStyle={imageStyle}
        onError={handleImageError}
        priority={priority}
        renderFallback={renderFallback}
        src={src}
      />
    </ImageWrapper>
  );

  // Render with or without AspectRatio wrapper based on sizing mode
  return (
    <>
      {containerConfig.useAspectRatioWrapper ? (
        <div
          className={cn(
            "relative overflow-hidden",
            roundedClass,
            className,
            containerClassName
          )}
          style={containerConfig.containerStyle}
        >
          <AspectRatio ratio={containerConfig.aspectRatio}>
            <ImageWrapper
              className={cn("size-full", isClickable && "cursor-pointer")}
              isClickable={isClickable}
              onClick={handleClick}
            >
              <ImageContent
                alt={alt}
                imageClasses={imageClasses}
                imageError={imageError}
                imageLoading={imageLoading}
                imageStyle={imageStyle}
                onError={handleImageError}
                priority={priority}
                renderFallback={renderFallback}
                src={src}
              />
            </ImageWrapper>
          </AspectRatio>
        </div>
      ) : (
        content
      )}
      {isOpen && (
        <ImageModal alt={alt} onClose={() => setIsOpen(false)} src={src} />
      )}
    </>
  );
}

function ImageModal({ src, alt, onClose }: ImageModalProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: Modal dialog requires keyboard handler for Escape key
    <div
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onKeyDown={handleKeyDown}
      role="dialog"
      tabIndex={-1}
    >
      <div className="relative max-h-[90vh] max-w-[90vw]">
        <button
          aria-label="Close image"
          className="absolute top-2 right-2 z-10 rounded-full bg-background/80 p-2 transition-colors hover:bg-background"
          onClick={onClose}
          type="button"
        >
          <svg
            fill="none"
            height="24"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            width="24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Close</title>
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
        <NextImage
          alt={alt}
          className="max-h-[90vh] max-w-[90vw] object-contain"
          height={1200}
          onClick={(e) => e.stopPropagation()}
          src={src}
          width={1200}
        />
      </div>
    </div>
  );
}

export { Image, ImageModal, type ImageProps, type ImageModalProps };
